import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerService } from '../services/partner.service';
import type { CreatePayoutRequest } from '../types/partner';

const PAYOUTS_KEY = 'partner-payouts';

export function usePartnerPayouts(params?: {
  page?: number;
  limit?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: [PAYOUTS_KEY, params],
    queryFn: () => partnerService.getPayouts(params),
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    retry: 2,
  });
}

export function useRequestPayout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePayoutRequest) => partnerService.requestPayout(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PAYOUTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['partner-dashboard'] });
    },
  });
}

export function usePartnerProfile() {
  return useQuery({
    queryKey: ['partner-profile'],
    queryFn: () => partnerService.getProfile(),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 15,
    retry: 2,
  });
}

export function useUpdatePartnerProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name?: string; phone?: string; pixKey?: string }) =>
      partnerService.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-profile'] });
    },
  });
}

export function useAddPixKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { type: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP'; key: string }) =>
      partnerService.addPixKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-profile'] });
    },
  });
}

export function useAddBankAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      bankName: string;
      bankCode: string;
      agency: string;
      account: string;
      accountType: 'CHECKING' | 'SAVINGS';
      holderName: string;
      holderCpf: string;
    }) => partnerService.addBankAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-profile'] });
    },
  });
}